/**
 * useOnlineStatus.js — global hook for connectivity + queue counters.
 *
 * Provides:
 *   • online (boolean)             — live navigator.onLine state
 *   • pendingChunks (number)       — total queued audio chunks across all sessions
 *   • pendingMutations (number)    — queued non-recording mutations
 *   • lastSyncedAt (number|null)   — ms epoch of last successful drain
 *   • drainNow()                   — manual retry button
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { listChunks } from '@/lib/chunkQueue';
import { countMutations } from '@/lib/offlineMutationQueue';
import { drainAll } from '@/lib/offlineApi';
import { API } from '@/App';

export default function useOnlineStatus() {
  const [online, setOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  const [pendingChunks, setPendingChunks] = useState(0);
  const [pendingMutations, setPendingMutations] = useState(0);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  const [draining, setDraining] = useState(false);
  const draining_ = useRef(false);

  const refreshCounts = useCallback(async () => {
    try {
      const [chunks, muts] = await Promise.all([listChunks(), countMutations()]);
      setPendingChunks(chunks.length);
      setPendingMutations(muts);
    } catch {
      // ignore — IDB unavailable in private mode etc.
    }
  }, []);

  const drainNow = useCallback(async () => {
    if (draining_.current) return;
    draining_.current = true;
    setDraining(true);
    try {
      const result = await drainAll(API);
      const movedChunks = result?.chunks?.drained || 0;
      const movedMuts = result?.mutations?.drained || 0;
      if (movedChunks + movedMuts > 0) setLastSyncedAt(Date.now());
    } finally {
      draining_.current = false;
      setDraining(false);
      refreshCounts();
    }
  }, [refreshCounts]);

  useEffect(() => {
    refreshCounts();

    const onOnline = () => {
      setOnline(true);
      drainNow();
    };
    const onOffline = () => setOnline(false);
    const onSWMessage = (event) => {
      if (event.data?.type === 'DRAIN_CHUNKS' || event.data?.type === 'DRAIN_MUTATIONS') {
        drainNow();
      }
    };
    // Refresh counters when app regains focus / becomes visible
    const onVisible = () => { if (!document.hidden) refreshCounts(); };

    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    document.addEventListener('visibilitychange', onVisible);
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', onSWMessage);
    }

    // Periodically refresh while offline so the badge counts stay accurate
    const tick = setInterval(refreshCounts, 8000);

    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      document.removeEventListener('visibilitychange', onVisible);
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', onSWMessage);
      }
      clearInterval(tick);
    };
  }, [drainNow, refreshCounts]);

  return {
    online,
    pendingChunks,
    pendingMutations,
    pendingTotal: pendingChunks + pendingMutations,
    lastSyncedAt,
    draining,
    drainNow,
    refreshCounts,
  };
}
